import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Upload,
  Sparkles,
  Link2,
  FileText,
  Trash2,
  Plus,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { generateScenarioContent } from "@/lib/scenarios/generateClient";
import {
  resolveSourceInputs,
  mimeUsesGeminiFiles,
  uploadFileToGemini,
  guessMime,
} from "@/lib/scenarios/resolveSourceInputs";
import { getMode, type InteractionMode } from "@/lib/scenarios/interactionModes";
import type { MCQQuestion } from "@/lib/powtoon-be/types";
import type { SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import { newDraftId } from "@/lib/scenarios/sourceDraft";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

import { partitionSourceFiles, runPool, WARMUP_CONCURRENCY } from "@/lib/scenarios/sourceFileBatch";

const ACCEPT = ".pdf,.docx,.pptx,.txt,.md,.csv,.json,image/*";

export type SourceInputValue = SourceInputDraft[];

export function SourceInput({
  mode,
  value,
  onChange,
  onGenerated,
  generating,
  onGeneratingChange,
  scenarioId,
  mcqOptions,
  onMcqOptionsChange,
  onMcqQuestions,
  hideGenerate,
}: {
  mode: InteractionMode;
  value: SourceInputValue;
  onChange: (v: SourceInputValue) => void;
  onGenerated: (content: string) => void;
  generating?: boolean;
  onGeneratingChange?: (b: boolean) => void;
  scenarioId?: string;
  mcqOptions?: { count: number; passRate: number };
  onMcqOptionsChange?: (next: { count: number; passRate: number }) => void;
  onMcqQuestions?: (questions: MCQQuestion[]) => void;
  /** Hide the generic per-mode "Generate" button. Used when the parent
   * editor has its own dedicated generation flow (e.g. role play). */
  hideGenerate?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [localBusy, setLocalBusy] = useState(false);
  const busy = generating ?? localBusy;
  const setBusy = (b: boolean) => {
    setLocalBusy(b);
    onGeneratingChange?.(b);
  };
  const [isDragging, setIsDragging] = useState(false);

  function updateAt(idx: number, next: SourceInputDraft) {
    onChange(value.map((it, i) => (i === idx ? next : it)));
  }
  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }
  function addItem(item: SourceInputDraft) {
    onChange([...valueRef.current, item]);
  }

  function handleFiles(files: File[]) {
    if (files.length === 0) return;
    const { accepted, rejected } = partitionSourceFiles(files, (f) =>
      mimeUsesGeminiFiles(f.type || guessMime(f.name), f.name),
    );
    if (rejected.length > 0) {
      toast.error(
        `Skipped ${rejected.length} file${rejected.length > 1 ? "s" : ""}: ${rejected
          .map((r) => `"${r.file.name}" (${r.reason})`)
          .join(", ")}`,
      );
    }
    if (accepted.length === 0) return;
    const drafts = accepted.map(({ file, usesGemini }) => ({
      kind: "upload" as const,
      _draftId: newDraftId(),
      file_name: file.name,
      size: file.size,
      mime: file.type || undefined,
      _file: file,
      gemini_upload_state: (usesGemini ? "preparing" : "ready") as "preparing" | "ready",
    }));
    const next = [...valueRef.current, ...drafts];
    valueRef.current = next;
    onChange(next);
    const toWarm = drafts.filter((d) => d.gemini_upload_state === "preparing");
    void runPool(toWarm, WARMUP_CONCURRENCY, warmupDraft);
  }

  // Several uploads may finish before a re-render; keep the ref current so none overwrite each other.
  function patchDraft(draft: SourceInputDraft) {
    const next = valueRef.current.map((it) => (it._draftId === draft._draftId ? draft : it));
    valueRef.current = next;
    onChange(next);
  }

  async function warmupDraft(draft: Extract<SourceInputDraft, { kind: "upload" }>) {
    try {
      const result = await uploadFileToGemini(draft);
      draft.gemini_file_uri = result.file_uri;
      draft.gemini_expires_at = result.expires_at;
      draft.gemini_upload_state = "ready";
      // Use the ref so we always patch the latest array, even if other drafts changed.
      patchDraft(draft);
    } catch (e) {
      draft.gemini_upload_state = "error";
      patchDraft(draft);
      toast.warning(`File preparation failed for "${draft.file_name}": ${(e as Error).message}`);
    }
  }

  const docCount = value.filter((it) => it.kind === "upload").length;
  const canGenerate = value.some((it) => {
    if (it.kind === "upload") return !!(it._file || it.object_id);
    if (it.kind === "url") return !!it.url.trim();
    return !!it.raw_text.trim();
  });

  async function handleGenerate() {
    setBusy(true);
    try {
      const inputs = await resolveSourceInputs(value, scenarioId);
      if (inputs.length === 0) {
        throw new Error("Add at least one source with content before generating");
      }
      const res = await generateScenarioContent({
        mode,
        inputs,
        ...(mode === "multiple_choice" && mcqOptions ? { mcq: { count: mcqOptions.count } } : {}),
      });
      onGenerated(res.content);
      if (mode === "multiple_choice" && res.questions && onMcqQuestions) {
        onMcqQuestions(res.questions);
      }
      res.warnings?.forEach((w) => toast.warning(`${w.file_name ?? "Source"}: ${w.error}`));
      toast.success("Content generated");
    } catch (e) {
      toast.error(`Generation failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDrop(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    handleFiles(files);
  }

  return (
    <section
      className={cn(
        "space-y-3 rounded-lg border bg-card p-4 transition-colors",
        isDragging && "border-primary ring-2 ring-primary/40 bg-primary/5",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIsDragging(false);
      }}
      onDrop={handleDrop}
    >
      <div>
        <h3 className="text-base font-semibold">Source input</h3>
        <p className="text-sm text-muted-foreground">
          Add any combination of files, URLs, and pasted text. All sources are sent to the AI
          together when you click Generate. PDFs give the fastest, most accurate analysis (up to
          20 MB). DOCX, PPTX and TXT up to 6 MB.
        </p>
      </div>

      <div className="space-y-2">
        {value.length === 0 && (
          <p className="text-xs text-muted-foreground italic px-1">
            No sources yet. Add at least one below.
          </p>
        )}
        {value.map((item, idx) => (
          <SourceItemCard
            key={idx}
            item={item}
            onChange={(next) => updateAt(idx, next)}
            onRemove={() => removeAt(idx)}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          <Upload className="h-3.5 w-3.5 mr-1" />
          Add file
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addItem({ kind: "url", _draftId: newDraftId(), url: "" })}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          <Link2 className="h-3.5 w-3.5 mr-1" />
          Add URL
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addItem({ kind: "text", _draftId: newDraftId(), raw_text: "" })}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          <FileText className="h-3.5 w-3.5 mr-1" />
          Add text
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => {
            handleFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
        <span className="self-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
          PDF recommended · up to 20 MB
        </span>
      </div>

      <div className="flex flex-wrap items-end justify-end gap-3 pt-1">
        {mode === "multiple_choice" && mcqOptions && onMcqOptionsChange && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Number of questions</Label>
            <Select
              value={String(mcqOptions.count)}
              onValueChange={(v) => onMcqOptionsChange({ ...mcqOptions, count: Number(v) })}
            >
              <SelectTrigger className="h-9 w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {!hideGenerate && (
          <Button type="button" onClick={handleGenerate} disabled={!canGenerate || busy}>
            <Sparkles className="h-4 w-4 mr-1.5" />
            {busy
              ? docCount > 1
                ? `Analyzing ${docCount} documents…`
                : "Generating…"
              : getMode(mode).generateLabel}
          </Button>
        )}
      </div>
    </section>
  );
}

function SourceItemCard({
  item,
  onChange,
  onRemove,
}: {
  item: SourceInputDraft;
  onChange: (next: SourceInputDraft) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-md border bg-background p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {item.kind === "upload" && <Upload className="h-3.5 w-3.5" />}
          {item.kind === "url" && <Link2 className="h-3.5 w-3.5" />}
          {item.kind === "text" && <FileText className="h-3.5 w-3.5" />}
          <span className="uppercase tracking-wide">{item.kind}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-7 px-2 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {item.kind === "upload" && <UploadItemMeta item={item} />}

      {item.kind === "url" && (
        <Input
          type="url"
          placeholder="https://example.com/source-article"
          value={item.url}
          onChange={(e) => onChange({ kind: "url", _draftId: item._draftId, url: e.target.value })}
        />
      )}

      {item.kind === "text" && (
        <Textarea
          placeholder="Paste source material here…"
          value={item.raw_text}
          onChange={(e) =>
            onChange({ kind: "text", _draftId: item._draftId, raw_text: e.target.value })
          }
          className="min-h-[120px]"
        />
      )}
    </div>
  );
}

function formatBytes(n?: number): string {
  if (!n || n <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function UploadItemMeta({ item }: { item: Extract<SourceInputDraft, { kind: "upload" }> }) {
  const parts: string[] = [];
  const size = formatBytes(item.size);
  if (size) parts.push(size);
  if (item.mime) parts.push(item.mime);
  if (item.object_id) parts.push("Stored");
  else if (item._file) parts.push("Local");

  const state = item.gemini_upload_state;
  const stateNode =
    state === "preparing" ? (
      <span className="inline-flex items-center gap-1 text-amber-600">
        <Loader2 className="h-3 w-3 animate-spin" />
        Preparing
      </span>
    ) : state === "ready" ? (
      <span className="inline-flex items-center gap-1 text-emerald-600">
        <CheckCircle2 className="h-3 w-3" />
        Ready
      </span>
    ) : state === "error" ? (
      <span className="inline-flex items-center gap-1 text-destructive">
        <AlertCircle className="h-3 w-3" />
        Preparation failed
      </span>
    ) : null;

  return (
    <div className="text-xs text-muted-foreground">
      <span className="text-foreground">{item.file_name}</span>
      {parts.length > 0 && ` · ${parts.join(" · ")}`}
      {stateNode && <span className="ml-2">{stateNode}</span>}
    </div>
  );
}
